import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {InputChangeEvent} from 'util/Types';
import {StageEntrySummary, StageRunSample} from 'workflows/WorkflowConstants';

const NEW_ENTRY_FROM_TEMPLATE = 'New Entry from Template';

interface BaseConfigureStageRunRowProps {
  stageName: string;
  stageEntry: StageEntrySummary;
  stageEntries: Array<StageEntrySummary>;
  onSelectStageEntry: (stageEntry: StageEntrySummary | null) => void;
  inputSampleIds: Array<string>;
  isInitialStageRun: boolean;
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample};
}

type ConfigureStageRunRowPropsWithEditing = BaseConfigureStageRunRowProps & {
  isEditingStageRun: true;
  name: string;
  onEditName: (e: InputChangeEvent) => void;
};

type ConfigureStageRunRowPropsWithoutEditing = BaseConfigureStageRunRowProps & {
  isEditingStageRun: false;
  name?: string;
  onEditName?: (e: InputChangeEvent) => void;
};

type ConfigureStageRunRowProps =
  | ConfigureStageRunRowPropsWithEditing
  | ConfigureStageRunRowPropsWithoutEditing;

class ConfigureStageRunRow extends React.Component<ConfigureStageRunRowProps> {
  _renderEntryInput(): JSX.Element {
    return (
      <SelectDropdown
        displayName={this.props.stageEntry ? this.props.stageEntry.name : NEW_ENTRY_FROM_TEMPLATE}
        menu={
          <Menu width={MenuWidth.FIT_CONTENT}>
            <Option
              onSelect={() => {
                this.props.onSelectStageEntry(null);
              }}
            >
              {NEW_ENTRY_FROM_TEMPLATE}
            </Option>
            {this.props.stageEntries.map((stageEntry) => (
              <Option
                key={stageEntry.id}
                onSelect={() => {
                  this.props.onSelectStageEntry(stageEntry);
                }}
              >
                {stageEntry.name}
              </Option>
            ))}
          </Menu>
        }
      />
    );
  }

  _renderInputSamples(): JSX.Element | string {
    if (this.props.inputSampleIds.length === 0) {
      if (this.props.isInitialStageRun) {
        return <span>None</span>;
      } else {
        return <span>Pending</span>;
      }
    } else if (
      this.props.inputSampleIds.some((inputSampleId) => !this.props.stageRunSampleById[inputSampleId])
    ) {
      return <span className="u-faded u-italic">Loading</span>;
    } else {
      const inputSampleNames = this.props.inputSampleIds.map(
        (inputSampleId) => this.props.stageRunSampleById[inputSampleId].name
      );
      return inputSampleNames.join(', ');
    }
  }

  _renderStageRunName(): JSX.Element | string {
    if (!this.props.isEditingStageRun) {
      return 'New';
    }

    if (BenchlingConfig.LEGACY_WORKFLOWS_ENABLE_STAGE_RUN_RENAME) {
      return (
        <div className="u-small-spacing-right">
          <TextField
            label="Name"
            labelStyle={LABEL_STYLES.HIDDEN}
            value={this.props.name}
            onChange={this.props.onEditName}
          />
        </div>
      );
    }
    return this.props.name;
  }

  override render(): JSX.Element {
    return (
      <div className="simpleDivTable-row">
        <div className="simpleDivTable-cell">
          <div className="u-small-spacing-right u-ellipsis-text">{this.props.stageName}</div>
        </div>
        <div className="simpleDivTable-cell">{this._renderInputSamples()}</div>
        <div className="simpleDivTable-cell">{this._renderStageRunName()}</div>
        <div className="simpleDivTable-cell">{this._renderEntryInput()}</div>
      </div>
    );
  }
}

export default ConfigureStageRunRow;
